import React from "react";
import { LoadingButton } from "@mui/lab";
import { Box, Divider, Stack, Typography } from "@mui/material";
import * as z from "zod";
import { TextField, useStudioForm } from "~/components/Form";
import { checkIsSignInDone } from "~/lib/auth";
import { invariant } from "~/lib/invariant";
import { requiredEmail, requiredPassword, requiredText } from "../common";
import { useConfirmSignUp, useRequestNewSignUpCode, useSignIn } from "./api";
import { AuthErrorAlert } from "./auth-error-alert";
import { RequestNewCodeButton } from "./request-new-code-button";
import { SocialAuth } from "./social-auth";

const signInSchema = z.object({
  email: requiredEmail,
  password: requiredPassword({
    minLength: 8,
    includesNumber: true,
    includesSpecial: true,
    includesUppercase: true,
    includesLowercase: true,
  }),
});

const confirmSignUpSchema = z.object({
  code: requiredText,
});

export function SignInForm({
  onLoadingStart,
  onLoadingEnd,
  onSignInComplete,
  message,
  forgotPassword,
  footer,
}: {
  onLoadingStart?: () => void;
  onLoadingEnd?: () => void;
  onSignInComplete: () => void;
  message?: React.ReactNode;
  forgotPassword?: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const signInMutation = useSignIn();

  const signInForm = useStudioForm({
    schema: signInSchema,
    onSubmit(values) {
      onLoadingStart?.();
      signInMutation.mutate(values, {
        onSuccess(response) {
          if (checkIsSignInDone(response)) {
            onSignInComplete();
          }
        },
        onSettled() {
          onLoadingEnd?.();
        },
      });
    },
    defaultValues: {
      email: null,
      password: null,
    },
  });

  const [email, password] = signInForm.watch(["email", "password"]);
  const canConfirmSignUp =
    email != null &&
    password != null &&
    signInMutation.isSuccess &&
    signInMutation.data.nextStep.signInStep === "CONFIRM_SIGN_UP";

  const confirmSignUpMutation = useConfirmSignUp();

  const confirmSignUpForm = useStudioForm({
    schema: confirmSignUpSchema,
    onSubmit(values) {
      invariant(canConfirmSignUp, "Not in state to confirm sign up");

      onLoadingStart?.();
      confirmSignUpMutation.mutate(
        { email, password, ...values },
        {
          onSuccess(response) {
            if (checkIsSignInDone(response)) {
              onSignInComplete();
            }
          },
          onSettled() {
            onLoadingEnd?.();
          },
        },
      );
    },
    defaultValues: {
      code: null,
    },
  });

  const requestNewCodeMutation = useRequestNewSignUpCode();

  let content;
  if (!signInMutation.isSuccess) {
    content = (
      <React.Fragment key="sign-in-step">
        {message}
        <Stack
          spacing={2}
          component="form"
          noValidate
          onSubmit={signInForm.handleSubmit}
        >
          <TextField
            control={signInForm.control}
            name="email"
            required
            type="username"
          />
          <TextField
            control={signInForm.control}
            name="password"
            required
            type="current-password"
          />
          {forgotPassword}
          <LoadingButton
            type="submit"
            color="primary"
            variant="contained"
            fullWidth
            disableElevation
            loading={signInMutation.isLoading}
          >
            Sign In
          </LoadingButton>
          <AuthErrorAlert mutation={signInMutation} />
        </Stack>
        <Divider>or</Divider>
        <SocialAuth type="sign-in" />
      </React.Fragment>
    );
  } else if (canConfirmSignUp) {
    content = (
      <Stack
        key="confirm-step"
        spacing={2}
        component="form"
        noValidate
        onSubmit={confirmSignUpForm.handleSubmit}
      >
        <Typography>
          You need to verify your email before you can sign in. Enter the
          verification code that was sent to your email.
        </Typography>
        <TextField
          control={confirmSignUpForm.control}
          name="code"
          required
          autoComplete="one-time-code"
        />
        <LoadingButton
          type="submit"
          color="primary"
          variant="contained"
          fullWidth
          disableElevation
          loading={confirmSignUpMutation.isLoading}
        >
          Verify Email
        </LoadingButton>
        <RequestNewCodeButton mutation={requestNewCodeMutation} email={email} />
        <AuthErrorAlert mutation={confirmSignUpMutation} />
      </Stack>
    );
  } else {
    content = null;
  }

  return (
    <Stack spacing={2}>
      {content}
      <Box sx={{ mt: 4 }}>{footer}</Box>
    </Stack>
  );
}
